/**
 * Som de notificação para a sala (ex.: novo pedido de acesso).
 *
 * Usa a Web Audio API para gerar um "chime" curto, evitando depender de um
 * arquivo de áudio externo. O AudioContext é reaproveitado entre chamadas e
 * só é criado no primeiro uso, depois que o moderador já interagiu com a
 * página (entrou na sala), o que satisfaz as políticas de autoplay.
 */

let sharedAudioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
    if (typeof window === "undefined") return null;

    const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

    if (!AudioCtx) return null;

    if (!sharedAudioContext) {
        try {
            sharedAudioContext = new AudioCtx();
        } catch {
            return null;
        }
    }

    return sharedAudioContext;
}

/** Toca um chime curto de dois tons. Nunca lança exceção. */
export function playAccessRequestSound(): void {
    try {
        const ctx = getAudioContext();
        if (!ctx) return;

        if (ctx.state === "suspended") {
            void ctx.resume().catch(() => {});
        }

        const now = ctx.currentTime;
        // Dois tons ascendentes (lá -> ré), suaves, ~0.6s no total.
        const tones: Array<{ freq: number; start: number; duration: number }> = [
            { freq: 880, start: 0, duration: 0.18 },
            { freq: 1174.66, start: 0.2, duration: 0.28 },
        ];

        const master = ctx.createGain();
        master.gain.value = 0.0001;
        master.connect(ctx.destination);

        for (const tone of tones) {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.value = tone.freq;

            const startAt = now + tone.start;
            const stopAt = startAt + tone.duration;

            // Envelope curto para evitar "clique" no início/fim.
            gain.gain.setValueAtTime(0.0001, startAt);
            gain.gain.exponentialRampToValueAtTime(0.35, startAt + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, stopAt);

            osc.connect(gain);
            gain.connect(master);

            osc.start(startAt);
            osc.stop(stopAt + 0.02);
        }

        master.gain.setValueAtTime(1, now);
    } catch {
        // Áudio é um reforço; nunca deve quebrar a sala.
    }
}
